import { Button, Checkbox, Chip, Dialog, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { fr } from 'date-fns/locale/fr';
import { Formik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import { Hospitalization } from '../../store/patientsSlice.ts';
import { PatientCompleteWizardType } from '../ui/WizardStep.tsx';

const psychiatricDisorderOptions = [
  'troubles dépressifs',
  'troubles bipolaires',
  'troubles anxieux',
  'troubles TDA',
  'trouble TDAH',
  'Autisme',
  'Schizophrénie',
];

const relapseOption = ['Oui', 'Non', 'Ne sais pas'];
const relationshipDegrees = [
  { value: 1, label: 'Degré 1 : parents, enfants, frères et soeurs' },
  { value: 2, label: 'Degré 2 : grands-parents, oncles et tantes, cousins' },
  { value: 3, label: 'Degré 3 : parents éloignés' },
];

const PatientCompleteWizardStep4: PatientCompleteWizardType = ({ formik }) => {
  const [showHospitalModal, setShowHospitalModal] = useState(false);
  const [newHospitalization, setNewHospitalization] = useState<Hospitalization>({
    id: undefined,
    hospital: '',
    reason: '',
    startDate: '',
    endDate: '',
  });

  const handleShowHospitalModal = () => {
    setShowHospitalModal(true);
  };
  const handleCloseHospitalModal = () => {
    setShowHospitalModal(false);
  };

  const handleAddHospitalization = (hospitalization: Hospitalization) => {
    const updatedHospitals = [...(formik.values.hospitalizations || []), { ...hospitalization, id: Date.now().toString() }];
    formik.setFieldValue('hospitalizations', updatedHospitals);
    handleCloseHospitalModal();
    setNewHospitalization({
      id: undefined,
      hospital: '',
      reason: '',
      startDate: '',
      endDate: '',
    });
  };

  const handleDeleteHospital = (id: string) => {
    const updatedHospitalizations = formik.values.hospitalizations?.filter((hosp) => hosp.id !== id);
    formik.setFieldValue('hospitalizations', updatedHospitalizations);
  };

  const dialogValidationSchema = yup.object().shape({
    hospital: yup.string().required("Nom de l'hôpital est requis"),
    reason: yup.string().required("Raison de l'hospitalisation est requise"),
    startDate: yup.date().required('Date de début est requise').typeError('Date de début invalide'),
    endDate: yup
      .date()
      .required('Date de fin est requise')
      .typeError('Date de fin invalide')
      .min(yup.ref('startDate'), 'Date de fin doit être après la date de début'),
  });

  return (
    <>
      <Grid container spacing={3}>
        <Grid offset={1} size={3}>
          <Typography>Antécédent psychiatrique familial</Typography>
        </Grid>
        <Grid container size={8} spacing={3}>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.familyPsychiatricDisorder || false}
                  color="primary"
                  name="familyPsychiatricDisorder"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              }
              label="Le patient a des antécédents psychiatriques familiaux"
            />
          </Grid>
          {formik.values.familyPsychiatricDisorder && (
            <Grid container size={12} spacing={3}>
              <Grid size={6}>
                <TextField
                  error={formik.touched.familyPsychiatricDisorders && Boolean(formik.errors.familyPsychiatricDisorders)}
                  fullWidth
                  helperText={formik.touched.familyPsychiatricDisorders && formik.errors.familyPsychiatricDisorders}
                  label="Pathologies psychiatriques"
                  name="psychiatricDisorders"
                  onChange={(event) => {
                    const { value } = event.target;
                    formik.setFieldValue('familyPsychiatricDisorders', typeof value === 'string' ? value.split(',') : value);
                  }}
                  select
                  slotProps={{ select: { multiple: true } }}
                  value={formik.values.familyPsychiatricDisorders || []}
                >
                  {psychiatricDisorderOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={6}>
                <TextField
                  error={formik.touched.familyPsychiatricDisorderRelationshipDegree && Boolean(formik.errors.familyPsychiatricDisorderRelationshipDegree)}
                  fullWidth
                  helperText={formik.touched.familyPsychiatricDisorderRelationshipDegree && formik.errors.familyPsychiatricDisorderRelationshipDegree}
                  label="Degré le plus proche uniquement"
                  name="familyPsychiatricDisorderRelationshipDegree"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder="Degré des membres de la famille concernés"
                  select
                  value={formik.values.familyPsychiatricDisorderRelationshipDegree}
                >
                  {relationshipDegrees.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid offset={1} size={3}>
          <Typography>Episodes antérieurs</Typography>
        </Grid>
        <Grid container size={8} spacing={3}>
          <Grid size={6}>
            <TextField
              error={formik.touched.episodes && Boolean(formik.errors.episodes)}
              fullWidth
              helperText={formik.touched.episodes && formik.errors.episodes}
              label="Nombre d'épisodes antérieurs"
              name="episodes"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="number"
              value={formik.values.episodes}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Tentative de suicide"
              name="suicideAttempts"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              placeholder="Valeur (0 possible)"
              type="number"
              value={formik.values.suicideAttempts}
            />
          </Grid>
          {(formik.values.episodes || 0) > 0 && (
            <Grid size={6}>
              <Typography>Si il y a eu un épisode, le patient a rechuté</Typography>
            </Grid>
          )}
          {(formik.values.episodes || 0) > 0 && (
            <Grid size={6}>
              <TextField
                error={formik.touched.relapsed && Boolean(formik.errors.relapsed)}
                fullWidth
                helperText={formik.touched.relapsed && formik.errors.relapsed}
                label="Rechute"
                name="relapsed"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                select
                value={formik.values.relapsed}
              >
                {relapseOption.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
        <Grid offset={1} size={3}>
          <Typography>Hospitalisations liées à un épisode dépressif</Typography>
        </Grid>
        <Grid size={8}>
          <Stack>
            {formik.values.hospitalizations?.map((hospital, index) => (
              <Chip
                key={index}
                label={`${hospital.hospital}: ${hospital.reason}, ${format(new Date(hospital.startDate), 'MMM yy')} - ${format(new Date(hospital.endDate), 'MMM yy')}`}
                onDelete={() => {
                  handleDeleteHospital(hospital.id || '');
                }}
                size="small"
                sx={{ color: 'primary.main', backgroundColor: 'primary.light', mb: 1, fontWeight: 'medium', borderColor: 'primary.main' }}
                variant="outlined"
              />
            ))}
            <Button onClick={handleShowHospitalModal} variant="outlined">
              + Ajouter une hospitalisation
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Dialog maxWidth="lg" onClose={handleCloseHospitalModal} open={showHospitalModal}>
        <DialogTitle>Ajouter une hospitalisation</DialogTitle>
        <DialogContent>
          <Formik initialValues={newHospitalization} onSubmit={handleAddHospitalization} validationSchema={dialogValidationSchema}>
            {(insideFormik) => (
              <LocalizationProvider adapterLocale={fr} dateAdapter={AdapterDateFns}>
                <form onSubmit={insideFormik.handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid size={3}>
                      <TextField
                        error={insideFormik.touched.hospital && !!insideFormik.errors.hospital}
                        fullWidth
                        helperText={insideFormik.touched.hospital && insideFormik.errors.hospital}
                        label="Nom de l'hôpital"
                        margin="normal"
                        name="hospital"
                        onBlur={insideFormik.handleBlur}
                        onChange={insideFormik.handleChange}
                        placeholder="Nom de l'hôpital"
                        value={insideFormik.values.hospital}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid size={3}>
                      <TextField
                        error={insideFormik.touched.reason && !!insideFormik.errors.reason}
                        fullWidth
                        helperText={insideFormik.touched.reason && insideFormik.errors.reason}
                        label="Raison"
                        margin="normal"
                        name="reason"
                        onBlur={insideFormik.handleBlur}
                        onChange={insideFormik.handleChange}
                        placeholder="Raison"
                        value={insideFormik.values.reason}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid size={3}>
                      <DatePicker
                        label="Date de début"
                        name="startDate"
                        onChange={(value) => insideFormik.setFieldValue('startDate', value)}
                        slotProps={{
                          textField: {
                            error: insideFormik.touched.startDate && Boolean(insideFormik.errors.startDate),
                            helperText: insideFormik.touched.startDate && insideFormik.errors.startDate,
                            onBlur: insideFormik.handleBlur,
                            fullWidth: true,
                            variant: 'outlined',
                            margin: 'normal',
                          },
                        }}
                        value={insideFormik.values.startDate !== '' ? new Date(insideFormik.values.startDate) : null}
                        views={['month', 'year']}
                      />
                    </Grid>
                    <Grid size={3}>
                      <DatePicker
                        label="Date de fin"
                        name="endDate"
                        onChange={(value) => {
                          void insideFormik.setFieldValue('endDate', value);
                        }}
                        slotProps={{
                          textField: {
                            error: insideFormik.touched.endDate && Boolean(insideFormik.errors.endDate),
                            helperText: insideFormik.touched.endDate && insideFormik.errors.endDate,
                            onBlur: insideFormik.handleBlur,
                            fullWidth: true,
                            variant: 'outlined',
                            margin: 'normal',
                          },
                        }}
                        value={insideFormik.values.endDate !== '' ? new Date(insideFormik.values.endDate) : null}
                        views={['month', 'year']}
                      />
                    </Grid>
                    <Grid justifyContent="flex-end" size={12}>
                      <Button color="secondary" onClick={handleCloseHospitalModal}>
                        Annuler
                      </Button>
                      <Button sx={{ float: 'right' }} type="submit" variant="contained">
                        Ajouter
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </LocalizationProvider>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
};

PatientCompleteWizardStep4.label = 'Historique de la maladie';

PatientCompleteWizardStep4.initialValues = {
  familyPsychiatricDisorder: false,
  familyPsychiatricDisorders: [],
  familyPsychiatricDisorderRelationshipDegree: 1,
  episodes: 0,
  relapsed: '',
  suicideAttempts: 0,
  hospitalizations: [],
};

PatientCompleteWizardStep4.validationSchema = yup.object().shape({
  familyPsychiatricDisorder: yup.boolean(),
  familyPsychiatricDisorders: yup
    .array()
    .of(yup.string().oneOf(psychiatricDisorderOptions, 'Sélectionner une option valide'))
    .when('familyPsychiatricDisorder', {
      is: true,
      then: (schema) => schema.min(1, 'Renseigner les troubles psychiatriques familiaux').required('Renseigner les troubles psychiatriques familiaux'),
    }),
  familyPsychiatricDisorderRelationshipDegree: yup
    .number()
    .oneOf([1, 2, 3], 'Sélectionner un degré valide')
    .when('familyPsychiatricDisorder', {
      is: true,
      then: (schema) => schema.required('Le degré de relation est requis'),
    }),
  episodes: yup.number().required("Nombre d'épisodes est requis"),
  relapsed: yup.string().when('episodes', {
    is: (episodes: number) => episodes > 0,
    then: (schema) => schema.required('Préciser si le patient a déjà rechuté'),
    otherwise: (schema) => schema.notRequired(),
  }),
  suicideAttempts: yup.number().required("Nombre de tentatives d'autolyse est requis"),
  hospitalizations: yup.array().of(
    yup.object().shape({
      hospital: yup.string().required("Nom de l'hôpital est requis"),
      reason: yup.string().required("Raison de l'hospitalisation est requise"),
      startDate: yup.date().required('Date de début est requise').typeError('Date de début invalide'),
      endDate: yup
        .date()
        .required('Date de fin est requise')
        .typeError('Date de fin invalide')
        .min(yup.ref('startDate'), 'Date de fin doit être après la date de début'),
    }),
  ),
});

export default PatientCompleteWizardStep4;
